import { useEffect, useMemo, useState } from "react";
import apiClient from "../../api/apiClient";
import API_CONFIG from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import useBusinessResource from "../../hooks/useBusinessResource";
import { catalogId } from "./catalogPresentation";

// Keep the authenticated client and the configured backend origin together.
export const catalogApi = Object.fromEntries(["get", "post", "put", "delete"].map((method) =>
  [method, (path, ...args) => apiClient[method](API_CONFIG.url(path), ...args)]));

export const catalogConfig = (companyId, signal, params = {}) => ({ signal,
  headers: { "X-Company-Id": String(companyId) }, params: { company_id: companyId, ...params } });

export async function readCatalogPages(path, key, companyId, signal, params = {}) {
  const result = [];
  for (let page = 1; ; page += 1) {
    const response = await catalogApi.get(path, catalogConfig(companyId, signal, { ...params, page, limit: 100 }));
    const rows = key ? response.data?.[key] : response.data;
    if (!Array.isArray(rows)) throw new Error("Les données du formulaire sont indisponibles.");
    result.push(...rows);
    const lastPage = Number(response.data?.pagination?.totalPages ?? 1);
    if (page >= lastPage) return result;
  }
}

export function useCatalogScope() {
  const auth = useAuth();
  const { selectedCompany, selectedWarehouse } = useSelection();
  const companies = useMemo(() => Array.from(new Map((auth.user?.assigned_warehouses || []).map((row) =>
    [String(row.company_id), { id: row.company_id, name: row.company_name || `Entreprise ${row.company_id}` }])).values()), [auth.user]);
  const companyId = catalogId(selectedCompany);
  const warehouseId = catalogId(selectedWarehouse);
  return { user: auth.user, hasPermission: auth.hasPermission, companyId, warehouseId, companies,
    companyName: selectedCompany?.name || companies.find((row) => String(row.id) === String(companyId))?.name || "Entreprise sélectionnée" };
}

export function useCatalogResource(resource, scope, load, parameters = [], active = true, enabled = true) {
  return useBusinessResource({ resource, userId: scope.user?.id, companyId: scope.companyId,
    warehouseId: scope.warehouseId, moduleScope: "commercial", scopeLevel: "company",
    parameters, active, enabled: Boolean(scope.companyId && enabled), load });
}

export function useCatalogSearch(value) {
  const [search, setSearch] = useState(value);
  useEffect(() => { const timer = setTimeout(() => setSearch(value), 250); return () => clearTimeout(timer); }, [value]);
  return search;
}
